import postService from '../services/posts';
import { createReactionThunk } from './reactionThunks';
import { notify } from './notificationReducer';
import getErrorMsg from '../utils/getErrorMsg';
import type { AppDispatch } from '../store';
import type { PostPageState, ReduxAction, CommentItem } from '../types/common';

const postPageReducer = (
  state: PostPageState | null = null,
  action: ReduxAction<unknown>
): PostPageState | null => {
  switch (action.type) {
    case 'FETCH_POST_COMMENTS':
      return action.payload as PostPageState;
    case 'CREATE_NEW_POST':
      return action.payload as PostPageState;
    case 'UPDATE_POST':
      return action.payload as PostPageState;
    case 'TOGGLE_VOTE':
      return { ...state, ...(action.payload as Record<string, unknown>) } as PostPageState;
    case 'TOGGLE_POSTPAGE_REACTION':
      return {
        ...state,
        ...((action.payload as { data?: Record<string, unknown> }).data || {}),
      } as PostPageState;
    case 'VOTE_COMMENT':
      return {
        ...state,
        comments: state?.comments.map((c: CommentItem) =>
          c.id !== (action.payload as { commentId: string }).commentId
            ? c
            : { ...c, ...((action.payload as { data?: Record<string, unknown> }).data || {}) }
        ) || [],
      } as PostPageState;
    case 'VOTE_REPLY':
      return {
        ...state,
        comments: state?.comments.map((c: CommentItem) =>
          c.id !== (action.payload as { commentId: string }).commentId
            ? c
            : {
                ...c,
                replies: c.replies.map((r) =>
                  r.id !== (action.payload as { replyId: string }).replyId
                    ? r
                    : { ...r, ...((action.payload as { data?: Record<string, unknown> }).data || {}) }
                ),
              }
        ) || [],
      } as PostPageState;
    case 'ADD_COMMENT':
      return {
        ...state,
        comments: [...(state?.comments || []), action.payload as CommentItem],
      } as PostPageState;
    case 'ADD_REPLY':
      return {
        ...state,
        comments: state?.comments.map((c: CommentItem) =>
          c.id !== (action.payload as { commentId: string }).commentId
            ? c
            : { ...c, replies: [...c.replies, (action.payload as { addedReply: CommentItem }).addedReply] }
        ) || [],
      } as PostPageState;
    case 'EDIT_COMMENT':
      return {
        ...state,
        comments: state?.comments.map((c: CommentItem) =>
          c.id !== (action.payload as { commentId: string }).commentId
            ? c
            : { ...c, ...((action.payload as { data?: Record<string, unknown> }).data || {}) }
        ) || [],
      } as PostPageState;
    case 'DELETE_COMMENT':
      return {
        ...state,
        comments: state?.comments.filter((c: CommentItem) => c.id !== (action.payload as string)) || [],
      } as PostPageState;
    case 'EDIT_REPLY':
      return {
        ...state,
        comments: state?.comments.map((c: CommentItem) =>
          c.id !== (action.payload as { commentId: string }).commentId
            ? c
            : {
                ...c,
                replies: c.replies.map((r) =>
                  r.id !== (action.payload as { replyId: string }).replyId
                    ? r
                    : { ...r, ...((action.payload as { data?: Record<string, unknown> }).data || {}) }
                ),
              }
        ) || [],
      } as PostPageState;
    case 'DELETE_REPLY':
      return {
        ...state,
        comments: state?.comments.map((c: CommentItem) =>
          c.id !== (action.payload as { commentId: string }).commentId
            ? c
            : {
                ...c,
                replies: c.replies.filter(
                  (r) => r.id !== (action.payload as { replyId: string }).replyId
                ),
              }
        ) || [],
      } as PostPageState;
    case 'SORT_COMMENTS':
      return {
        ...state,
        comments: (state?.comments || []).sort((a, b) => {
          switch (action.payload) {
            case 'new':
              return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
            case 'upvoted':
              return b.pointsCount - a.pointsCount;
            case 'downvoted':
              return a.pointsCount - b.pointsCount;
            case 'replied':
              return b.replies.length - a.replies.length;
            default:
              return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          }
        }),
      } as PostPageState;
    default:
      return state;
  }
};

export const fetchPostComments = (id: string) => {
  return async (dispatch: AppDispatch) => {
    const fetchedPost = await postService.getPostComments(id);

    dispatch({
      type: 'FETCH_POST_COMMENTS',
      payload: fetchedPost,
    });
  };
};

export const createNewPost = (postObject: Record<string, unknown>) => {
  return async (dispatch: AppDispatch) => {
    const addedPost = await postService.addNew(postObject);

    dispatch({
      type: 'CREATE_NEW_POST',
      payload: addedPost,
    });

    // Surface the new post at the top of the home feed immediately so the
    // list reflects it without a hard refresh.
    dispatch({
      type: 'ADD_NEW_POST',
      payload: addedPost,
    });

    return (addedPost as { id: string }).id;
  };
};

export const updatePost = (id: string, postObject: Record<string, unknown>) => {
  return async (dispatch: AppDispatch) => {
    const updatedPost = await postService.editPost(id, postObject);

    dispatch({
      type: 'UPDATE_POST',
      payload: updatedPost,
    });
  };
};

export const toggleUpvote = (id: string, upvotedBy: string[], downvotedBy: string[]) => {
  return async (dispatch: AppDispatch) => {
    let pointsCount = upvotedBy.length - downvotedBy.length;
    if (pointsCount < 0) {
      pointsCount = 0;
    }

    dispatch({
      type: 'TOGGLE_VOTE',
      payload: { upvotedBy, pointsCount, downvotedBy },
    });

    await postService.upvotePost(id);
  };
};

export const toggleDownvote = (id: string, downvotedBy: string[], upvotedBy: string[]) => {
  return async (dispatch: AppDispatch) => {
    let pointsCount = upvotedBy.length - downvotedBy.length;
    if (pointsCount < 0) {
      pointsCount = 0;
    }

    dispatch({
      type: 'TOGGLE_VOTE',
      payload: { upvotedBy, pointsCount, downvotedBy },
    });

    await postService.downvotePost(id);
  };
};

export const toggleReaction = createReactionThunk('TOGGLE_POSTPAGE_REACTION');

// Reactions on comments/replies are authoritative like the post-level thunk:
// call the idempotent add/remove endpoint and merge the returned state.
// `held` says whether the user currently has this reaction, so the call is an
// explicit remove rather than a toggle. Reuses VOTE_COMMENT/VOTE_REPLY.
export const toggleCommentReaction = (
  postId: string,
  commentId: string,
  reactionKey: string,
  held: boolean
) => {
  return async (dispatch: AppDispatch) => {
    try {
      const data = await postService.setCommentReaction(postId, commentId, reactionKey, !held);
      dispatch({
        type: 'VOTE_COMMENT',
        payload: {
          commentId,
          data: { reactions: data.reactions, reactionEntries: data.reactionEntries },
        },
      });
    } catch (err) {
      dispatch(notify(getErrorMsg(err), 'error'));
    }
  };
};

export const toggleReplyReaction = (
  postId: string,
  commentId: string,
  replyId: string,
  reactionKey: string,
  held: boolean
) => {
  return async (dispatch: AppDispatch) => {
    try {
      const data = await postService.setReplyReaction(
        postId,
        commentId,
        replyId,
        reactionKey,
        !held
      );
      dispatch({
        type: 'VOTE_REPLY',
        payload: {
          commentId,
          replyId,
          data: { reactions: data.reactions, reactionEntries: data.reactionEntries },
        },
      });
    } catch (err) {
      dispatch(notify(getErrorMsg(err), 'error'));
    }
  };
};

export const toggleCommentUpvote = (
  postId: string,
  commentId: string,
  upvotedBy: string[],
  downvotedBy: string[]
) => {
  return async (dispatch: AppDispatch) => {
    const pointsCount = upvotedBy.length - downvotedBy.length;

    dispatch({
      type: 'VOTE_COMMENT',
      payload: { commentId, data: { upvotedBy, pointsCount, downvotedBy } },
    });

    await postService.upvoteComment(postId, commentId);
  };
};

export const toggleCommentDownvote = (
  postId: string,
  commentId: string,
  downvotedBy: string[],
  upvotedBy: string[]
) => {
  return async (dispatch: AppDispatch) => {
    const pointsCount = upvotedBy.length - downvotedBy.length;

    dispatch({
      type: 'VOTE_COMMENT',
      payload: { commentId, data: { upvotedBy, pointsCount, downvotedBy } },
    });

    await postService.downvoteComment(postId, commentId);
  };
};

export const toggleReplyUpvote = (
  postId: string,
  commentId: string,
  replyId: string,
  upvotedBy: string[],
  downvotedBy: string[]
) => {
  return async (dispatch: AppDispatch) => {
    const pointsCount = upvotedBy.length - downvotedBy.length;

    dispatch({
      type: 'VOTE_REPLY',
      payload: {
        commentId,
        replyId,
        data: { upvotedBy, pointsCount, downvotedBy },
      },
    });

    await postService.upvoteReply(postId, commentId, replyId);
  };
};

export const toggleReplyDownvote = (
  postId: string,
  commentId: string,
  replyId: string,
  downvotedBy: string[],
  upvotedBy: string[]
) => {
  return async (dispatch: AppDispatch) => {
    const pointsCount = upvotedBy.length - downvotedBy.length;

    dispatch({
      type: 'VOTE_REPLY',
      payload: {
        commentId,
        replyId,
        data: { upvotedBy, pointsCount, downvotedBy },
      },
    });

    await postService.downvoteReply(postId, commentId, replyId);
  };
};

export const addComment = (postId: string, comment: string) => {
  return async (dispatch: AppDispatch) => {
    const addedComment = await postService.postComment(postId, { comment });

    dispatch({
      type: 'ADD_COMMENT',
      payload: addedComment,
    });
  };
};

export const addReply = (postId: string, commentId: string, reply: string) => {
  return async (dispatch: AppDispatch) => {
    const addedReply = await postService.postReply(postId, commentId, {
      reply,
    });

    dispatch({
      type: 'ADD_REPLY',
      payload: { commentId, addedReply },
    });
  };
};

export const editComment = (postId: string, commentId: string, comment: string) => {
  return async (dispatch: AppDispatch) => {
    await postService.updateComment(postId, commentId, { comment });
    const updatedAt = Date.now();

    dispatch({
      type: 'EDIT_COMMENT',
      payload: { commentId, data: { updatedAt, commentBody: comment } },
    });
  };
};

export const deleteComment = (postId: string, commentId: string) => {
  return async (dispatch: AppDispatch) => {
    await postService.removeComment(postId, commentId);

    dispatch({
      type: 'DELETE_COMMENT',
      payload: commentId,
    });
  };
};

export const editReply = (postId: string, commentId: string, replyId: string, reply: string) => {
  return async (dispatch: AppDispatch) => {
    await postService.updateReply(postId, commentId, replyId, { reply });
    const updatedAt = Date.now();

    dispatch({
      type: 'EDIT_REPLY',
      payload: { commentId, replyId, data: { updatedAt, replyBody: reply } },
    });
  };
};

export const deleteReply = (postId: string, commentId: string, replyId: string) => {
  return async (dispatch: AppDispatch) => {
    await postService.removeReply(postId, commentId, replyId);

    dispatch({
      type: 'DELETE_REPLY',
      payload: { commentId, replyId },
    });
  };
};

export const sortComments = (sortBy: string) => {
  return (dispatch: AppDispatch) => {
    dispatch({
      type: 'SORT_COMMENTS',
      payload: sortBy,
    });
  };
};

export default postPageReducer;
